/**
 * Meta Pixel client-side helper.
 *
 * Auto-injects the fbq base script only when the admin has saved a Pixel ID
 * (via /admin/afrika-shop → Meta Pixel). All event helpers no-op safely
 * when not initialised.
 *
 * Browser events that should later be deduped by the server's Conversions
 * API use a shared `eventID` — return it from `track()` and persist it on
 * the order so the M-Pesa webhook can fire the matching CAPI event.
 *
 * Playbook-verified shapes:
 *   ViewContent      → { content_ids, content_type, value, currency, content_name }
 *   AddToCart        → { content_ids, content_type, value, currency, contents:[{id,quantity,item_price}] }
 *   InitiateCheckout → { content_ids, contents, value, currency, num_items }
 *   Purchase         → { content_ids, contents, value, currency, num_items }
 */
import { hasMarketingConsent } from './privacyConsent';

const API = process.env.REACT_APP_BACKEND_URL;

let _initialised = false;
let _pixelId = null;
let _testEventCode = null;
let _initPromise = null;

const CURRENCY = 'KES';

const _injectScript = (pixelId) => {
  if (typeof window === 'undefined') return;
  if (window.fbq) return;
  /* eslint-disable */
  (function(f,b,e,v,n,t,s){
    if(f.fbq) return; n=f.fbq=function(){n.callMethod ?
      n.callMethod.apply(n,arguments) : n.queue.push(arguments) };
    if(!f._fbq) f._fbq=n; n.push=n; n.loaded=!0; n.version='2.0';
    n.queue=[]; t=b.createElement(e); t.async=!0;
    t.src=v; s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', pixelId);
};

/** Idempotently fetch the public Pixel config and inject the script if set. */
export const initMetaPixel = async () => {
  if (!hasMarketingConsent()) return false;
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    try {
      const res = await fetch(`${API}/api/meta-pixel/public`);
      const data = await res.json();
      if (!data?.pixel_id) return false;
      _pixelId = data.pixel_id;
      _testEventCode = data.test_event_code || null;
      _injectScript(_pixelId);
      // PageView is fired separately by trackPageView once a route mounts.
      _initialised = true;
      return true;
    } catch {
      return false;
    }
  })();
  return _initPromise;
};

const _genEventId = (name) =>
  `${name}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const _track = (eventName, params = {}, customEventId = null) => {
  if (!hasMarketingConsent() || !_initialised || !window.fbq) return null;
  const eventId = customEventId || _genEventId(eventName);
  try {
    const opts = { eventID: eventId };
    if (_testEventCode) opts.test_event_code = _testEventCode;
    window.fbq('track', eventName, params, opts);
  } catch (e) {
    // never block app flow on a tracker failure
    // eslint-disable-next-line no-console
    console.warn('[meta-pixel] track failed', e);
  }
  return eventId;
};

export const trackPageView = () => _track('PageView');

// ── Browser → Server CAPI mirror helpers (iter 169) ──────────────────────
// Pair every browser fbq('track', ...) with a server-side CAPI POST so
// events flow even when ad-blockers strip the client-side Pixel. Meta
// dedupes the pair via shared event_id.
const _BACKEND = process.env.REACT_APP_BACKEND_URL;
const _mirrorCapi = (route, payload) => {
  if (typeof window === 'undefined' || !_BACKEND || !hasMarketingConsent()) return;
  try {
    // Best-effort, non-blocking. Failures here must never bubble to the UI.
    fetch(`${_BACKEND}/api/meta-pixel/capi/${route}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,  // survives page nav, useful for AddToCart on click-away
    }).catch(() => {});
  } catch (_) { /* never throws */ }
};

export const trackViewContent = ({ sku, productId, title, priceKes }) => {
  const eventId = _genEventId('ViewContent');
  _track('ViewContent', {
    content_ids: [sku || productId].filter(Boolean),
    content_type: 'product',
    content_name: title,
    value: Number(priceKes || 0),
    currency: CURRENCY,
  }, eventId);
  const { fbp, fbc } = readFbCookies();
  _mirrorCapi('view-content', {
    product_id: productId || '',
    sku: sku || null,
    title: title || '',
    price_kes: Number(priceKes || 0),
    event_id: eventId,
    source_url: typeof window !== 'undefined' ? window.location.href : '',
    fbp, fbc,
  });
  return eventId;
};

export const trackAddToCart = ({ sku, productId, title, priceKes, quantity = 1 }) => {
  const eventId = _genEventId('AddToCart');
  _track('AddToCart', {
    content_ids: [sku || productId].filter(Boolean),
    content_type: 'product',
    content_name: title,
    value: Number(priceKes || 0) * Number(quantity || 1),
    currency: CURRENCY,
    contents: [{ id: sku || productId, quantity: Number(quantity || 1), item_price: Number(priceKes || 0) }],
  }, eventId);
  const { fbp, fbc } = readFbCookies();
  _mirrorCapi('add-to-cart', {
    product_id: productId || '',
    sku: sku || null,
    title: title || '',
    price_kes: Number(priceKes || 0),
    quantity: Number(quantity || 1),
    event_id: eventId,
    source_url: typeof window !== 'undefined' ? window.location.href : '',
    fbp, fbc,
  });
  return eventId;
};

export const trackInitiateCheckout = ({ items, valueKes }) =>
  _track('InitiateCheckout', {
    content_ids: items.map((i) => i.sku || i.product_id).filter(Boolean),
    content_type: 'product',
    contents: items.map((i) => ({
      id: i.sku || i.product_id,
      quantity: Number(i.quantity || 1),
      item_price: Number(i.unit_price_kes || i.price_kes || 0),
    })),
    num_items: items.reduce((n, i) => n + Number(i.quantity || 1), 0),
    value: Number(valueKes || 0),
    currency: CURRENCY,
  });

/** Returns the eventID. Persist it on the order for CAPI dedup. */
export const trackPurchase = ({ items, valueKes, eventId }) =>
  _track(
    'Purchase',
    {
      content_ids: items.map((i) => i.sku || i.product_id).filter(Boolean),
      content_type: 'product',
      contents: items.map((i) => ({
        id: i.sku || i.product_id,
        quantity: Number(i.quantity || 1),
        item_price: Number(i.unit_price_kes || i.price_kes || 0),
      })),
      num_items: items.reduce((n, i) => n + Number(i.quantity || 1), 0),
      value: Number(valueKes || 0),
      currency: CURRENCY,
    },
    eventId,
  );

// ── Wider-site custom events ────────────────────────────────────────────
// Custom events (vs Meta's Standard events) still create Custom Conversions
// in Events Manager. They're indexed and usable for retargeting audiences
// like "people who played a mixtape but never bought merch."
//
// Naming convention: PascalCase. Use them in Meta Ads Manager → Audiences
// → Custom Audiences → "People who triggered <event name>".

/** Reggae/Dancehall mixtape playback — high-intent music audience signal. */
export const trackMixtapePlay = ({ mixtapeId, title, dj }) =>
  _track('MixtapePlay', {
    content_ids: [mixtapeId].filter(Boolean),
    content_name: title || 'Mixtape',
    content_category: 'mixtape',
    dj_name: dj || '',
  });

/** Tapped on an event card or 'Buy Tickets' link — event-buyer intent. */
export const trackEventInterest = ({ eventId, title, priceKes }) =>
  _track('EventInterest', {
    content_ids: [eventId].filter(Boolean),
    content_name: title || 'Event',
    content_category: 'event',
    value: Number(priceKes || 0),
    currency: CURRENCY,
  });

/** Newsletter / sign-up / contact-form completion — lead capture. */
export const trackLead = ({ source, valueKes = 0 }) =>
  _track('Lead', {
    content_category: source || 'signup',
    value: Number(valueKes || 0),
    currency: CURRENCY,
  });

/** Search box used (e.g. afrika shop search, mixtape search). */
export const trackSearch = ({ query, resultCount }) =>
  _track('Search', {
    search_string: (query || '').slice(0, 120),
    content_category: 'site',
    num_items: Number(resultCount || 0),
  });

/** Reggae Trivia / Daily Challenge completion — engagement audience. */
export const trackContentEngagement = ({ contentType, contentId, label }) =>
  _track('CustomEngagement', {
    content_ids: [contentId].filter(Boolean),
    content_category: contentType,
    content_name: label || '',
  });

/** Read the _fbp / _fbc cookies (Facebook's first-party click ids) for CAPI dedup. */
export const readFbCookies = () => {
  if (typeof document === 'undefined') return { fbp: null, fbc: null };
  const get = (name) => {
    const m = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : null;
  };
  return { fbp: get('_fbp'), fbc: get('_fbc') };
};

/** Generate (or reuse) a deterministic per-flow event_id paired with the CAPI Purchase mirror. */
export const newPurchaseEventId = () => _genEventId('Purchase');
